import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { CopyButton } from '@/components/shared/CopyButton'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import { toast } from 'sonner'
import type { Domain, Alias, Mailbox } from '@/types'
import {
  ArrowLeft, Plus, Trash2, Mail, CheckCircle2, XCircle,
  Loader2, Users, Route, RotateCcw, AlertCircle,
  Copy, Send, Globe, Wand2, Shield
} from 'lucide-react'
import { DomainSetupWizard } from '@/components/shared/DomainSetupWizard'

export function DomainDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [domain, setDomain] = useState<Domain | null>(null)
  const [aliases, setAliases] = useState<Alias[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [loading, setLoading] = useState(true)
  const [showAliasDialog, setShowAliasDialog] = useState(false)
  const [newAliasSource, setNewAliasSource] = useState('')
  const [newAliasTargets, setNewAliasTargets] = useState<string[]>([])
  const [aliasLoading, setAliasLoading] = useState(false)
  const [showCatchAllDialog, setShowCatchAllDialog] = useState(false)
  const [catchAllTarget, setCatchAllTarget] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  // CHECKPOINT: PRD-09 FR-9.1.4 — Domain ownership TXT verification state.
  const [txtRecord, setTxtRecord] = useState<{ name: string; value: string } | null>(null)
  const [txtVerified, setTxtVerified] = useState(false)
  const [txtVerifying, setTxtVerifying] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [showPermanentDialog, setShowPermanentDialog] = useState(false)
  const [permanentDeleting, setPermanentDeleting] = useState(false)

  const fetchData = async () => {
    if (!id) return
    try {
      const [domainRes, aliasesRes, mailboxesRes, verificationRes] = await Promise.all([
        api.get(`/domains/${id}`),
        api.get(`/domains/${id}/aliases`),
        api.get('/mailboxes', { params: { domain_id: id } }),
        api.get(`/domains/${id}/verification`).catch(() => ({ data: null })),
      ])
      setDomain(domainRes.data)
      setAliases(aliasesRes.data.aliases || [])
      setMailboxes(mailboxesRes.data.mailboxes || [])
      const v = verificationRes.data
      if (v) {
        setTxtRecord({
          name: v.txt_record_name || v.record_name || `_purpletoad.${domainRes.data.name}`,
          value: v.txt_record_value || v.record_value || v.value || '',
        })
        setTxtVerified(v.verified === true || v.status === 'verified')
      }
    } catch {
      navigate('/domains')
    } finally {
      setLoading(false)
    }
  }

  // CHECKPOINT: PRD-02 FR-2.2.4 — Customer-initiated soft delete with 30-day grace.
  const handleDeleteDomain = async () => {
    if (!id || !domain) return
    if (deleteConfirmName.toLowerCase() !== domain.name.toLowerCase()) {
      toast.error('Domain name confirmation does not match')
      return
    }
    setDeleting(true)
    try {
      await api.delete(`/domains/${id}`)
      toast.success('Domain scheduled for deletion. You can restore it for 30 days.')
      setShowDeleteDialog(false)
      setDeleteConfirmName('')
      fetchData()
    } catch (err: any) {
      const detail = err.response?.data?.detail
      toast.error(
        detail?.message || detail || 'Failed to delete domain'
      )
    } finally {
      setDeleting(false)
    }
  }

  const handlePermanentDeleteDomain = async () => {
    if (!id || !domain) return
    if (deleteConfirmName.toLowerCase() !== domain.name.toLowerCase()) {
      toast.error('Domain name confirmation does not match')
      return
    }
    setPermanentDeleting(true)
    try {
      await api.post(`/domains/${id}/permanent-delete`, { domain_name: domain.name })
      toast.success('Domain deleted permanently')
      navigate('/domains')
    } catch (err: any) {
      const detail = err.response?.data?.detail
      toast.error(detail?.message || detail || 'Failed to delete domain')
    } finally {
      setPermanentDeleting(false)
    }
  }

  const handleVerifyTxt = async () => {
    if (!id) return
    setTxtVerifying(true)
    try {
      const res = await api.post(`/domains/${id}/verify`)
      const data = res.data
      if (data.verified === true || data.status === 'verified') {
        setTxtVerified(true)
        toast.success('Domain ownership TXT verified')
      } else {
        toast.error('TXT record not found or incorrect')
      }
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'TXT verification failed')
    } finally {
      setTxtVerifying(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const openAliasDialog = () => {
    // CHECKPOINT: PRD-10 FR-10.13.2 — Reset alias creation form when dialog opens.
    setNewAliasSource('')
    setNewAliasTargets([])
    setShowAliasDialog(true)
  }

  const handleAddAlias = async () => {
    if (!id || !newAliasSource.trim() || newAliasTargets.length === 0) return
    setAliasLoading(true)
    try {
      await api.post(`/domains/${id}/aliases`, {
        domain_id: id,
        source: newAliasSource.trim(),
        targets: newAliasTargets,
        enabled: true,
      })
      setShowAliasDialog(false)
      setNewAliasSource('')
      setNewAliasTargets([])
      fetchData()
      toast.success('Alias created')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to create alias')
    } finally {
      setAliasLoading(false)
    }
  }

  const handleDeleteAlias = async (aliasId: string) => {
    if (!confirm('Delete this alias?')) return
    try {
      await api.delete(`/aliases/${aliasId}`)
      fetchData()
      toast.success('Alias deleted')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to delete alias')
    }
  }

  const handleSetCatchAll = async () => {
    if (!id) return
    try {
      await api.post(`/domains/${id}/catch-all`, {
        target_mailbox_id: catchAllTarget || null,
      })
      setShowCatchAllDialog(false)
      fetchData()
      toast.success('Catch-all updated')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to update catch-all')
    }
  }

  const handleVerifyDns = async () => {
    if (!id) return
    setVerifying(true)
    try {
      const res = await api.post(`/domains/${id}/verify-dns`)
      const data = res.data
      if (data.all_verified || data.dev_bypass) {
        toast.success(data.dev_bypass ? 'Domain activated (dev bypass)' : 'DNS verified! Domain is now active.')
      } else {
        toast.error('DNS records not yet verified. Check the records below and try again.')
      }
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'DNS verification failed')
    } finally {
      setVerifying(false)
    }
  }

  const handleRestore = async () => {
    if (!id) return
    try {
      await api.post(`/domains/${id}/restore`)
      fetchData()
      toast.success('Domain restored')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to restore domain')
    }
  }

  const copyAllRecords = () => {
    if (!domain?.dns_records) return
    const lines = Object.entries(domain.dns_records).map(([, rec]) => {
      const prio = rec.priority ? `${rec.priority} ` : ''
      return `${rec.type}\t${rec.host}\t${prio}${rec.value}`
    })
    navigator.clipboard.writeText(lines.join('\n'))
    toast.success('All DNS records copied')
  }

  const handleSendTest = async () => {
    if (!testEmail || !mailboxes.length) return
    setSendingTest(true)
    try {
      await api.post('/outbound/send', {
        from_email: mailboxes[0].email,
        to: [testEmail],
        subject: `Test email from ${domain?.name}`,
        text: `This is a test email sent from ${domain?.name} via PurpleToad Mail.`,
        html: `<p>This is a test email sent from <strong>${domain?.name}</strong> via PurpleToad Mail.</p>`,
      })
      toast.success('Test email sent! Check your inbox.')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to send test email')
    } finally {
      setSendingTest(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </div>
    )
  }

  if (!domain) return null

  const allVerified = domain.dns_health && Object.values(domain.dns_health).every(
    (h) => h.status === 'verified'
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/domains')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{domain.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <StatusBadge status={domain.status} />
            <span className="text-sm text-muted-foreground">
              {mailboxes.length} mailboxes · {aliases.length} aliases · {domain.quota_used_mb} MB / {domain.quota_mb} MB
            </span>
          </div>
        </div>
        {domain.status === 'pending' && (
          <Button variant="outline" size="sm" onClick={handleVerifyDns} disabled={verifying}>
            {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Verify DNS
          </Button>
        )}
      </div>

      {/* Pending banner */}
      {domain.status === 'pending' && (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Domain pending DNS verification</p>
                  <p className="text-sm text-muted-foreground">
                    Add the DNS records below to your DNS provider, then click "Verify DNS".
                    Propagation can take a few minutes to several hours.
                  </p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setWizardOpen(true)}>
                <Wand2 className="mr-2 h-4 w-4" />
                Setup Wizard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="dns" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dns">DNS Records</TabsTrigger>
          <TabsTrigger value="aliases">Aliases</TabsTrigger>
          <TabsTrigger value="mailboxes">Mailboxes</TabsTrigger>
          {domain.status === 'active' && <TabsTrigger value="test">Test</TabsTrigger>}
        </TabsList>

        <TabsContent value="dns" className="space-y-4">
          {/* CHECKPOINT: PRD-09 FR-9.1.4 — Domain ownership TXT record card. */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4 text-violet-400" />
                Domain Ownership
              </CardTitle>
              {txtVerified ? (
                <span className="text-xs text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                </span>
              ) : (
                <Button size="sm" variant="outline" onClick={handleVerifyTxt} disabled={txtVerifying}>
                  {txtVerifying && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  Verify TXT
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${txtVerified ? 'border-emerald-500/20 bg-emerald-500/5' : 'bg-accent/30'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase w-10">TXT</span>
                    <span className="text-sm font-mono">{txtRecord?.name || `_purpletoad.${domain.name}`}</span>
                  </div>
                  <CopyButton text={txtRecord?.name || `_purpletoad.${domain.name}`} />
                </div>
                <div className="flex items-start gap-2 text-sm font-mono text-muted-foreground pl-[52px]">
                  <span className="text-xs shrink-0">→</span>
                  <span className="break-all">{txtRecord?.value || 'Loading...'}</span>
                </div>
              </div>
              {!txtVerified && (
                <p className="text-xs text-muted-foreground">
                  Add the TXT record above to prove ownership of {domain.name}. Outbound sending is disabled until this is verified.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-sm font-medium">Required DNS Records</CardTitle>
              <Button variant="ghost" size="sm" onClick={copyAllRecords}>
                <Copy className="mr-2 h-3.5 w-3.5" />
                Copy All
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {domain.dns_records && Object.entries(domain.dns_records).map(([key, record]: [string, any]) => {
                const health = domain.dns_health?.[key]
                const isVerified = health?.status === 'verified'
                const isMissing = health?.status === 'missing' || health?.status === 'mismatch' || health?.status === 'error' || health?.status === 'timeout'
                return (
                  <div key={key} className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${isVerified ? 'border-green-500/20 bg-green-500/5' : isMissing ? 'border-red-500/20 bg-red-500/5' : 'bg-accent/30'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold uppercase w-10">{record.type}</span>
                        <span className="text-sm font-mono">{record.host}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isVerified ? (
                          <CheckCircle2 className="h-4 w-4 text-green-400" />
                        ) : isMissing ? (
                          <XCircle className="h-4 w-4 text-red-400" />
                        ) : (
                          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                        )}
                        <CopyButton text={`${record.type}\t${record.host}\t${record.priority ? record.priority + ' ' : ''}${record.value}`} />
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-sm font-mono text-muted-foreground pl-[52px]">
                      <span className="text-xs shrink-0">→</span>
                      <span className="break-all">{record.value}</span>
                    </div>
                    {health?.error && (
                      <p className="text-xs text-red-400 pl-[52px]">{health.error}</p>
                    )}
                    {health?.actual && health.status !== 'verified' && (
                      <p className="text-xs text-muted-foreground pl-[52px]">
                        Found: {health.actual}
                      </p>
                    )}
                  </div>
                )
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Route className="h-4 w-4 text-violet-400" />
                Catch-All
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Catch-all receives emails sent to any address at this domain that doesn't match a mailbox or alias.
              </p>
              {domain.catch_all_enabled ? (
                <div className="flex items-center justify-between bg-accent/30 rounded-lg px-3 py-2">
                  <span className="text-sm">Catch-all enabled → {domain.catch_all_target || 'Unknown'}</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowCatchAllDialog(true)}>
                    Change
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setShowCatchAllDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Set Catch-All
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aliases" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Aliases</h3>
            <Button size="sm" onClick={openAliasDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alias
            </Button>
          </div>

          {aliases.length === 0 ? (
            <EmptyState
              title="No aliases yet"
              description="Create aliases to forward emails to one or more mailboxes."
              icon={<Mail className="h-5 w-5" />}
            />
          ) : (
            <div className="grid gap-3">
              {aliases.map((alias) => {
                const targetEmails = alias.targets
                  .map((id) => mailboxes.find((m) => m.id === id)?.email || id)
                  .join(', ')
                return (
                  <Card key={alias.id} className="hover:bg-accent/20 transition-colors">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-violet-400" />
                          <span className="text-sm font-semibold">{alias.source}</span>
                          {alias.is_catch_all && (
                            <span className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">
                              catch-all
                            </span>
                          )}
                          {!alias.enabled && (
                            <span className="text-[10px] bg-slate-500/10 text-slate-400 px-1.5 py-0.5 rounded">
                              disabled
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          → {targetEmails}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteAlias(alias.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mailboxes" className="space-y-4">
          <div className="grid gap-3">
            {mailboxes.map((mbx) => (
              <Card
                key={mbx.id}
                className="hover:bg-accent/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/mailboxes/${mbx.id}`)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      <Users className="h-5 w-5 text-violet-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{mbx.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {mbx.quota_used_mb} MB / {mbx.quota_mb} MB · {mbx.status}
                      </p>
                    </div>
                  </div>
                  <ArrowLeft className="h-4 w-4 text-muted-foreground rotate-180" />
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {domain.status === 'active' && (
          <TabsContent value="test" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Send className="h-4 w-4 text-violet-400" />
                  Send Test Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Send a test email from <strong>{mailboxes[0]?.email || 'your mailbox'}</strong> to verify outbound delivery.
                  {mailboxes.length === 0 && ' Create a mailbox first.'}
                </p>
                {/* CHECKPOINT: PRD-09 FR-9.1.4 — Disable test email until domain ownership TXT is verified. */}
                <div className="flex gap-2">
                  <Input
                    placeholder="recipient@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    disabled={mailboxes.length === 0 || !txtVerified}
                  />
                  <Button
                    onClick={handleSendTest}
                    disabled={!testEmail || sendingTest || mailboxes.length === 0 || !txtVerified}
                    title={!txtVerified ? 'Verify domain ownership TXT first' : undefined}
                  >
                    {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                {import.meta.env.DEV && (
                  <p className="text-xs text-muted-foreground">
                    In development, emails are caught by Mailhog: <a href="http://localhost:8025" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline">http://localhost:8025</a>
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Globe className="h-4 w-4 text-violet-400" />
                  DNS Health Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {domain.dns_health && Object.entries(domain.dns_health).map(([key, health]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-muted-foreground">{key}</span>
                    <span className={health.status === 'verified' ? 'text-green-400' : 'text-red-400'}>
                      {health.status === 'verified' ? 'Verified' : health.error || health.status}
                    </span>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Overall</span>
                    <span className={allVerified ? 'text-green-400' : 'text-amber-400'}>
                      {allVerified ? 'All records verified' : 'Some records pending'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* CHECKPOINT: PRD-02 FR-2.2.4 — Customer delete schedules 30-day grace. */}
      {domain.status === 'deleting' ? (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-500">
              <AlertCircle className="h-4 w-4" />
              Grace Period
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This domain is scheduled for deletion. You can restore it until{' '}
              {domain.grace_period_end ? new Date(domain.grace_period_end).toLocaleString() : 'the grace period ends'}.
            </p>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleRestore}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Restore Domain
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setShowPermanentDialog(true)}>
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Permanently
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-red-500/20 bg-red-500/5">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-500">
              <AlertCircle className="h-4 w-4" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Deleting this domain will schedule it for removal. It will enter a 30-day grace
              period during which you can restore it and all its mailboxes.
            </p>
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Domain
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Alias Dialog */}
      <Dialog open={showAliasDialog} onOpenChange={setShowAliasDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Alias</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Source Address</label>
              <Input
                placeholder={`support@${domain.name}`}
                value={newAliasSource}
                onChange={(e) => setNewAliasSource(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Target Mailboxes</label>
              <select
                multiple
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newAliasTargets}
                onChange={(e) => {
                  const opts = Array.from(e.target.selectedOptions).map(o => o.value)
                  setNewAliasTargets(opts)
                }}
              >
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>{m.email}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAliasDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddAlias}
              disabled={aliasLoading || !newAliasSource || newAliasTargets.length === 0}
            >
              {aliasLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Alias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Catch-All Dialog */}
      <Dialog open={showCatchAllDialog} onOpenChange={setShowCatchAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Catch-All Target</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Select a mailbox to receive all unmatched emails for this domain.
            </p>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={catchAllTarget}
              onChange={(e) => setCatchAllTarget(e.target.value)}
            >
              <option value="">Disable catch-all</option>
              {mailboxes.map((m) => (
                <option key={m.id} value={m.id}>{m.email}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatchAllDialog(false)}>Cancel</Button>
            <Button onClick={handleSetCatchAll}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Domain Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Domain</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This will schedule <strong>{domain?.name}</strong> for deletion. It will enter a 30-day grace
              period; during that time you can restore it and all its mailboxes and aliases.
              Type the domain name below to confirm.
            </p>
            <Input
              placeholder={`Type ${domain?.name || 'domain'} to confirm`}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteDomain}
              disabled={deleting || deleteConfirmName.toLowerCase() !== (domain?.name || '').toLowerCase()}
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHECKPOINT: PRD-02 FR-2.2.5 — Permanent delete dialog only shown for deleting domains. */}
      {/* Permanent Delete Dialog */}
      <Dialog open={showPermanentDialog} onOpenChange={setShowPermanentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Domain Permanently</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This will permanently delete <strong>{domain?.name}</strong> and all its mailboxes and aliases.
              This cannot be undone.
            </p>
            <Input
              placeholder={`Type ${domain?.name || 'domain'} to confirm`}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermanentDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handlePermanentDeleteDomain}
              disabled={permanentDeleting || deleteConfirmName.toLowerCase() !== (domain?.name || '').toLowerCase()}
            >
              {permanentDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DomainSetupWizard
        domain={domain}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onUpdated={fetchData}
      />
    </div>
  )
}
